import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import httpClient from '../../../httpClient';
import ChevronIcon from '../../../images/chevron-down-12.svg';
import styles from '../../../pages/ProductSelection.module.css';
import useProductSelectionStore from '../../../stores/useProductSelectionStore';


const deriveMatTypeFromMatFormat = (matFormatText) => {
  const text = (matFormatText || '').trim();
  if (/^double\s*mat\s*4[\s-]*ply$/i.test(text)) return 'double-mat-4-ply';
  if (/^8[\s-]*ply$/i.test(text)) return '8-ply';
  if (/^4[\s-]*ply$/i.test(text)) return '4-ply';
  return undefined;
};

const OPENING_SHAPE_LABELS = { standard: 'Standard', circle: 'Circle', oval: 'Oval' };

const MattedPrintsFromInputs = ({ quantityRef }) => {
  const {
    form,
    setForm,
    processPrintSizeData,
    setPrintSizeLoading,
    resetPrintSizeForm,
    setFinishingServiceLoading,
    processFinishingCategoriesData,
    processFinishingServicesData,
    processFinishingServiceChange,
    processFrameMatPricingData,
    finishingCategoriesData,
    finishingServicesData,
    lineItem,
    isEditInitializing,
  } = useProductSelectionStore();

  const matFormatInput = form?.inputs?.matFormat;
  const matOpeningShapeInput = form?.inputs?.matOpeningShape;
  const finishingCategoryId = form?.meta?.finishingCategoryId;
  const printSizeGuid = form?.inputs?.printSize?.selectedValue;

  // Sourced from the same reference list Framing/Circle-Oval Framing use,
  // scoped server-side to this print size + category's own finishing
  // service pricings, then filtered client-side to the chosen opening shape.
  const frameMatsQuery = useQuery({
    queryKey: ['frameMats', printSizeGuid, finishingCategoryId],
    queryFn: () =>
      httpClient.get(
        `products/frame_mats?print_size_guid=${printSizeGuid}&finishing_category_guid=${finishingCategoryId}`,
      ),
    enabled: !!finishingCategoryId && !!printSizeGuid,
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => {
      processFrameMatPricingData(response);
    },
  });

  const frameMatRows = frameMatsQuery.data?.data ?? [];
  const derivedMatType = deriveMatTypeFromMatFormat(matFormatInput?.selectedValueText);
  const selectedFrameMatPricing = frameMatRows.find(
    (row) =>
      row.attributes.matType === derivedMatType &&
      row.attributes.openingShape === matOpeningShapeInput?.selectedValue,
  );
  const frameMatPricingGuid = selectedFrameMatPricing?.id;

  useEffect(() => {
    if (form?.meta?.frameMatPricingGuid === frameMatPricingGuid) return;
    setForm((prev) => ({
      ...prev,
      meta: { ...prev.meta, frameMatPricingGuid },
    }));
  }, [frameMatPricingGuid]);

  // Safety net for restoring Mat Opening Shape (and Mat Type) on edit, same
  // reasoning as the Mat Size safety net below: processFrameMatPricingData
  // only runs from frameMatsQuery's onSuccess, a one-time callback that can
  // fire before lineItem/isEditInitializing are both ready, with no retry
  // since staleTime: Infinity never refetches. The function itself already
  // no-ops safely if shouldAutoMap/matching fails, so it's safe to just
  // re-invoke it reactively whenever the real data becomes available.
  //
  // finishingServicesData is also a dependency here even though this effect
  // only reads frame mat data - processFinishingServicesData's Matting
  // branch unconditionally clears inputs.matOpeningShape.selectedValue back
  // to undefined every time it runs (see useProductSelectionStore.js), and
  // it fires from a separate query with no ordering guarantee against this
  // one. Without this dependency, whichever query resolves second wins the
  // race: if finishingServices resolves after frame_mats, it silently wipes
  // the opening shape this effect had just restored. Depending on both means
  // this effect re-applies the restore again after either one settles, so it
  // always wins regardless of network timing.
  useEffect(() => {
    if (!frameMatsQuery.data) return;
    processFrameMatPricingData(frameMatsQuery.data);
  }, [frameMatsQuery.data, finishingServicesData, lineItem, isEditInitializing]);

  // Mat Opening Shape options, straight from the real frame_mats rows for
  // the selected mat type — not the aspect-ratio guess processFinishingServicesData
  // seeds the field with while this data is still loading (see its Matting
  // branch in useProductSelectionStore.js). Skipped when the shape is already
  // forced by an Oval/Circle Deckle paper selection, which resolves to a
  // single, hidden option on its own.
  const paperTypeText = (form?.inputs?.paperType?.selectedValueText || '').toLowerCase();
  const isForcedOpeningShape = paperTypeText.includes('circle') || paperTypeText.includes('oval');
  const matTypeShapes = frameMatRows
    .filter((row) => row.attributes.matType === derivedMatType)
    .map((row) => row.attributes.openingShape);
  const realOpeningShapeOptions = derivedMatType
    ? Array.from(new Set(matTypeShapes)).map((value) => ({
        value,
        text: OPENING_SHAPE_LABELS[value] || value,
      }))
    : [];
  const realOpeningShapeValues = realOpeningShapeOptions.map((o) => o.value).join(',');

  useEffect(() => {
    if (isForcedOpeningShape) return;
    if (!frameMatsQuery.data) return;
    if (!derivedMatType) return;
    if (!matOpeningShapeInput) return;
    if (realOpeningShapeOptions.length === 0) return;

    const singleOption =
      realOpeningShapeOptions.length === 1 ? realOpeningShapeOptions[0] : undefined;

    setForm((prev) => ({
      ...prev,
      meta: {
        ...prev.meta,
        ...(singleOption && { matOpeningShape: singleOption.value }),
        requiredInputs: singleOption
          ? (prev.meta.requiredInputs || []).filter((label) => label !== 'Mat Opening Shape')
          : prev.meta.requiredInputs,
      },
      inputs: {
        ...prev.inputs,
        matOpeningShape: {
          ...prev.inputs.matOpeningShape,
          options: realOpeningShapeOptions,
          isHidden: !!singleOption,
          ...(singleOption && {
            selectedValue: singleOption.value,
            selectedValueText: singleOption.text,
          }),
        },
      },
    }));
  }, [
    isForcedOpeningShape,
    frameMatsQuery.data,
    derivedMatType,
    realOpeningShapeValues,
    !!matOpeningShapeInput,
  ]);

  // Safety net for restoring Mat Size on edit: processFinishingServicesData
  // only runs once, from the finishingServices query's onSuccess callback,
  // at the exact moment that request resolves. If lineItem/isEditInitializing
  // aren't both ready yet at that instant, the match is silently skipped and
  // (with staleTime: Infinity) never retried. This effect re-checks reactively
  // whenever the real data actually becomes available, independent of when
  // the original request happened to resolve.
  useEffect(() => {
    if (!lineItem || !isEditInitializing) return;
    if (!finishingServicesData) return;
    const matSizeInput = form?.inputs?.matSize;
    if (!matSizeInput) return;
    if (matSizeInput.selectedValue && matSizeInput.selectedValue !== 'default') return;

    // Guid match only works if the line item's original price list is still
    // the active one - see the identical fallback in
    // processFinishingServicesData for why name match is needed too.
    const serviceGuid = lineItem?.attributes?.finishingService?.finishingServiceGuid;
    const serviceName = lineItem?.attributes?.finishingService?.finishingService;
    if (!serviceGuid && !serviceName) return;

    const options = matSizeInput.options || [];
    const matchedOption =
      options.find((option) => option.value === serviceGuid) ||
      options.find((option) => option.name === serviceName);
    if (!matchedOption) return;

    setForm((prev) => ({
      ...prev,
      meta: {
        ...prev.meta,
        finishingServiceId: matchedOption.value,
        requiredInputs: (prev.meta.requiredInputs || []).filter((label) => label !== 'Mat Size'),
      },
      inputs: {
        ...prev.inputs,
        matSize: {
          ...prev.inputs.matSize,
          selectedValue: matchedOption.value,
          selectedValueText: matchedOption.text,
          selectedValuePrice: matchedOption.price,
        },
      },
    }));
  }, [finishingServicesData, lineItem, isEditInitializing, form?.inputs?.matSize?.options]);

  const paperTypeSelectedValue = form?.inputs?.paperType?.selectedValue;
  const printSizeSelectedValue = form?.inputs?.printSize?.selectedValue;

  const printSizesQuery = useQuery({
    queryKey: ['printSizes', paperTypeSelectedValue],
    queryFn: () => httpClient.get(`products/${paperTypeSelectedValue}/print_sizes`),
    enabled: !!paperTypeSelectedValue && paperTypeSelectedValue !== 'default',
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => processPrintSizeData(response),
  });

  useEffect(() => {
    if (paperTypeSelectedValue === 'default') {
      resetPrintSizeForm();
    } else if (paperTypeSelectedValue && printSizesQuery.isLoading) {
      setPrintSizeLoading(true);
    }
  }, [paperTypeSelectedValue, printSizesQuery.isLoading]);

  const finishingCategoriesQuery = useQuery({
    queryKey: ['finishingCategories', printSizeSelectedValue],
    queryFn: () =>
      httpClient.get(
        `products/print_sizes/${printSizeSelectedValue}/finishing_categories`,
      ),
    enabled: !!printSizeSelectedValue && printSizeSelectedValue !== 'default',
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => processFinishingCategoriesData(response),
  });

  useEffect(() => {
    if (
      printSizeSelectedValue &&
      printSizeSelectedValue !== 'default' &&
      finishingCategoriesQuery.isLoading
    ) {
      setFinishingServiceLoading(true);
    }
  }, [printSizeSelectedValue, finishingCategoriesQuery.isLoading]);

  useQuery({
    queryKey: ['finishingServices', printSizeSelectedValue, finishingCategoryId],
    queryFn: () =>
      httpClient.get(
        `products/print_sizes/${printSizeSelectedValue}/finishing_categories/${finishingCategoryId}/finishing_services`,
      ),
    enabled: !!printSizeSelectedValue && !!finishingCategoryId,
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => processFinishingServicesData(response),
  });

  useEffect(() => {
    const options = form?.inputs?.finishingService?.options;
    if (!options?.length) return;
    if (form?.inputs?.finishingService?.selectedValue) return;

    const mattingOption = options.find((opt) => opt?.text === 'Matting');
    if (!mattingOption) return;

    setForm((prev) => ({
      ...prev,
      inputs: {
        ...prev.inputs,
        finishingService: {
          ...prev.inputs.finishingService,
          selectedValue: mattingOption.value,
          selectedValueText: mattingOption.text,
        },
      },
    }));
  }, [form?.inputs?.finishingService?.options, form?.inputs?.finishingService?.selectedValue, setForm]);

  useEffect(() => {
    if (form?.inputs?.finishingService?.selectedValueText === undefined) return;
    processFinishingServiceChange(form.inputs.finishingService.selectedValueText);
    // Also re-run once finishingCategoriesData actually arrives - during edit
    // initialization, finishingService.selectedValueText can get set to
    // "Matting" before the finishing_categories request resolves.
    // processFinishingServiceChange early-returns while that data is still
    // null, so without this dependency the retry never happens and Mat
    // Format (and everything below it) is left stuck on "Choose One".
  }, [form?.inputs?.finishingService?.selectedValueText, finishingCategoriesData]);

  const handleInputChange = (key, e) => {
    const value = e.target.value;
    const selectedText = e.target.options[e.target.selectedIndex].text;
    const selectedOption = (form?.inputs?.[key]?.options || []).find(
      (o) => o?.value?.toString() === value,
    );

    if (key === 'paperType') {
      if (value === 'default') {
        resetPrintSizeForm();
        return;
      }
      setForm((prev) => ({
        ...prev,
        inputs: {
          ...prev.inputs,
          paperType: {
            ...prev.inputs.paperType,
            selectedValue: value,
            selectedValueText: selectedText,
          },
          printSize: {
            ...prev.inputs.printSize,
            selectedValue: undefined,
            selectedValueText: undefined,
            selectedValuePrice: undefined,
          },
        },
      }));
      return;
    }

    if (key === 'printSize') {
      if (value === 'default') {
        setForm((prev) => ({
          ...prev,
          meta: {
            ...prev.meta,
            xAspectRatio: undefined,
            yAspectRatio: undefined,
            aspectRatio: undefined,
            finishingCategoryId: undefined,
            finishingServiceId: undefined,
            requiredInputs: ['Print Size'],
          },
          inputs: {
            paperType: prev.inputs.paperType,
            printSize: {
              ...prev.inputs.printSize,
              selectedValue: value,
              selectedValueText: selectedText,
              selectedValuePrice: undefined,
            },
            finishingService: prev.inputs.finishingService,
          },
        }));
        return;
      }

      setForm((prev) => ({
        ...prev,
        meta: {
          ...prev.meta,
          xAspectRatio: selectedOption?.xAspectRatio,
          yAspectRatio: selectedOption?.yAspectRatio,
          aspectRatio:
            selectedOption?.xAspectRatio && selectedOption?.yAspectRatio
              ? selectedOption.xAspectRatio / selectedOption.yAspectRatio
              : undefined,
        },
        inputs: {
          ...prev.inputs,
          printSize: {
            ...prev.inputs.printSize,
            selectedValue: value,
            selectedValueText: selectedText,
            selectedValuePrice: selectedOption?.price,
          },
        },
      }));
      return;
    }

    if (key === 'matFormat') {
      const categoryId = value !== 'default'
        ? (selectedOption?.categoryId || value)
        : undefined;
      setForm((prev) => ({
        meta: {
          ...prev.meta,
          finishingCategoryId: categoryId,
          finishingServiceId: undefined,
          requiredInputs: value && value !== 'default' ? ['Mat Size'] : ['Mat Format'],
        },
        inputs: {
          ...prev.inputs,
          matFormat: {
            ...prev.inputs.matFormat,
            selectedValue: value,
            selectedValueText: selectedText,
          },
          ...(prev.inputs.matSize && {
            matSize: {
              ...prev.inputs.matSize,
              selectedValue: undefined,
              selectedValueText: undefined,
              selectedValuePrice: undefined,
            },
          }),
        },
      }));
      return;
    }

    if (key === 'matSize') {
      setForm((prev) => {
        const shapeSelected = prev.inputs.matOpeningShape?.selectedValue && prev.inputs.matOpeningShape?.selectedValue !== 'default';
        return {
          ...prev,
          meta: {
            ...prev.meta,
            finishingServiceId: value !== 'default' ? value : undefined,
            requiredInputs: value && value !== 'default' ? (shapeSelected ? [] : ['Mat Opening Shape']) : ['Mat Size'],
          },
          inputs: {
            ...prev.inputs,
            matSize: {
              ...prev.inputs.matSize,
              selectedValue: value,
              selectedValueText: selectedText,
              selectedValuePrice: selectedOption?.price,
            },
          },
        };
      });
      return;
    }

    if (key === 'matOpeningShape') {
      setForm((prev) => {
        const sizeSelected = prev.inputs.matSize?.selectedValue && prev.inputs.matSize?.selectedValue !== 'default';
        const cropShape = (value === 'circle' || value === 'oval') ? value : undefined;
        return {
          ...prev,
          meta: {
            ...prev.meta,
            matOpeningShape: cropShape,
            requiredInputs: value && value !== 'default' ? (sizeSelected ? [] : ['Mat Size']) : ['Mat Opening Shape'],
          },
          inputs: {
            ...prev.inputs,
            matOpeningShape: {
              ...prev.inputs.matOpeningShape,
              selectedValue: value,
              selectedValueText: selectedText,
            },
          },
        };
      });
      return;
    }
  };

  if (!form || !form.inputs) return null;

  return (
    <div className={styles.printsFormStack}>
      <div className="input-container quantity">
        <div className="label-container">
          <div className="div-block-150">
            <div className="sub-label-form">Quantity</div>
          </div>
        </div>
        <div className="form-block-input w-form">
          <div className="input-wrapper">
            <input
              ref={quantityRef}
              type="number"
              min="1"
              maxLength="256"
              name="field-quantity"
              defaultValue="1"
              className="text-field-2 data-jscolor w-input"
              pattern="[0-9]*"
              inputMode="numeric"
            />
          </div>
        </div>
      </div>

      {Object.keys(form.inputs)
        .filter((key) => {
          if (key === 'finishingService') return false;
          if (form.inputs[key]?.isHidden) return false;
          return true;
        })
        .map((key) => {
          const input = form.inputs[key];
          if (!input) return null;

          return (
            <div key={key} style={input.style}>
              <div className="input-container paper-type">
                <div className="label-container">
                  <div className="div-block-150">
                    <div className="sub-label-form">{input.label || key}</div>
                  </div>
                </div>
                <div className="form-block-input w-form">
                  <form className="form-input select-field">
                    <select
                      className={`form-select-field w-select select-field-wrapper ${styles.selectNoNativeArrow}`}
                      style={{ width: '100%' }}
                      onChange={(e) => handleInputChange(key, e)}
                      value={input.selectedValue || 'default'}
                      disabled={input.isDisabled}
                    >
                      {input.isLoading === true &&
                        (input.options?.length || 0) === 0 ? (
                        <option value="">Loading...</option>
                      ) : (
                        <option value="default">Choose One</option>
                      )}
                      {(input.options || [])
                        .filter((option) => {
                          if (!option?.text) return false;
                          if (option.text.endsWith('**')) return false;
                          if (key === 'paperType') {
                            const t = option.text.toLowerCase();
                            return !t.includes('canvas') && !t.includes('lustre');
                          }
                          return true;
                        })
                        .map((option) => {
                          if (!option?.value || !option?.text) return null;
                          const displayText =
                            key === 'paperType'
                              ? option.text.replace(/\(\$\d+(\.\d{1,2})?\)/g, '').trim()
                              : option.text;
                          return (
                            <option key={option.value} value={option.value}>
                              {displayText}
                            </option>
                          );
                        })
                        .filter(Boolean)}
                    </select>
                    <img
                      src={ChevronIcon}
                      alt=""
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                        width: '16px',
                        height: '16px',
                      }}
                    />
                  </form>
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default MattedPrintsFromInputs;